<template>
  <div class="container">
    <div class="p-booking__inner-wrapper">
      <div class="s-hero-slider__title mb-3">
        {{ dictionary.title }}
      </div>
      <div class="p-booking__title pt-4">{{ dictionary.paymentTitle }}</div>
      <div
        class="p-booking__title-desc mb-4"
        v-html="dictionary.paymentDescription"></div>
      <Loader v-if="isLoading"></Loader>
      <Alert
        v-if="!isLoading && errorMsgs.length"
        key="submit-errors"
        dismiss-by-native=""
        type="danger"
        :messages="errorMsgs"
        @close="removeErrors"></Alert>
      <div class="p-booking__tickets-wrapper">
        <div class="p-booking__tickets-choice">
          <form autocomplete="off" @submit.prevent="onSubmitForm">
            <div class="b-block b-block--transparent b-block--sm-pad">
              <div class="b-block__form-section">
                <!-- fullName -->
                <div class="form-floating">
                  <input
                    id="firstName"
                    v-model="firstName"
                    disabled
                    readonly
                    type="text"
                    class="form-control"
                    :placeholder="dictionary.firstNameLabel" />
                  <label for="firstName">{{ dictionary.firstNameLabel }}</label>
                </div>

                <div class="form-floating">
                  <input
                    id="lastName"
                    v-model="lastName"
                    disabled
                    readonly
                    type="text"
                    class="form-control"
                    :placeholder="dictionary.lastNameLabel" />
                  <label for="lastName">{{ dictionary.lastNameLabel }}</label>
                </div>

                <!-- email -->
                <div class="form-floating">
                  <input
                    id="email"
                    v-model="email"
                    disabled
                    readonly
                    type="email"
                    class="form-control"
                    :placeholder="dictionary.emailLabel" />
                  <label for="email">{{ dictionary.emailLabel }}</label>
                </div>

                <!-- mobilePhone -->
                <div class="form-floating">
                  <input
                    id="mobilePhone"
                    v-model="mobilePhone"
                    disabled
                    readonly
                    type="text"
                    class="form-control"
                    :placeholder="dictionary.mobilePhoneLabel" />
                  <label for="mobilePhone">
                    {{ dictionary.mobilePhoneLabel }}
                  </label>
                </div>
              </div>
            </div>

            <div class="mt-4">
              <div class="custom-chck custom-chck--additional-style">
                <input
                  id="user-terms"
                  v-model="terms"
                  autocomplete="off"
                  type="checkbox"
                  :class="{ 'form-control--error': v$.terms.$error }"
                  @blur="v$.terms.$touch" />
                <label for="user-terms">
                  <span class="custom-chck__span">
                    <div class="custom-chck__text">
                      <em>{{ dictionary.termsHyperLabel }}</em>
                      <span v-html="dictionary.termsLabel"></span>
                    </div>
                  </span>
                </label>
              </div>
              <template v-if="v$.terms.$error">
                <div
                  v-if="v$.terms.checked.$invalid"
                  class="field-validation-error">
                  {{ dictionary.termsRequired }}
                </div>
              </template>
              <button
                :id="buttonPaymentSubmitId"
                type="submit"
                class="d-none"
                :disabled="buttonSubmitIsDisabled"></button>
            </div>
          </form>
        </div>

        <div class="p-booking__tickets-total">
          <div class="p-booking__shop__summary">
            <CartSummary />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div>
    <SinglePaymentProgress
      class="b-booking-progress--contained"
      :is-valid="isValid"
      @go-to-previous="cancelProcess"
      @go-to-next="goToNextStep" />
  </div>
</template>

<script>
// CORE
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// STORE
import { useBookingStore } from '../store/BookingStore'

// LIB
import constants from '../../lib/constants'
import { getData, scrollToError, scrollToValidation } from '../../lib/lib'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'

// COMPONENTS
import CartSummary from '../components/Booking/Shared/CartSummary.vue'
import SinglePaymentProgress from '../components/SinglePaymentProgress.vue'
import Loader from '../components/Shared/Loader.vue'
import Alert from '../components/Shared/Alert.vue'

export default {
  components: {
    CartSummary,
    SinglePaymentProgress,
    Loader,
    Alert,
  },
  setup() {
    return { store: useBookingStore(), v$: useVuelidate() }
  },
  data() {
    return {
      shopItems: null,
      cinemas: null,
      currentStep: 1,
      additionalPayload: null,

      editProfileEnabled: false,

      isLoading: false,
      errorMsgs: [],
      buttonPaymentSubmitId: constants.buttonPaymentSubmitId,

      fullName: '',
      email: '',
      mobilePhone: '',

      terms: '',
    }
  },
  validations() {
    return {
      terms: {
        checked: (value) => value === true,
      },
    }
  },
  computed: {
    buttonSubmitIsDisabled() {
      return this.isLoading || this.v$.$invalid
    },
    isValid() {
      return this.isLoading || this.v$.$invalid
    },
  },
  beforeMount() {
    const { user } = this.configuration
    this.firstName = user.firstName
    this.lastName = user.lastName
    this.email = user.email
    this.mobilePhone = user.mobilePhone

    const { shopItems } = getData('singlePaymentData')
    this.shopItems = shopItems

    this.shopItems.forEach((element) => {
      this.store.addNewUserProduct(element)
    })
    const { userSessionId } = this.configuration
    this.store.setUsersessionId(userSessionId)

  },
  methods: {
    cancelProcess() {
      const url = this.configuration.cancelUrl
      if (url) {
        window.location.assign(this.configuration.cancelUrl)
      } else {
        console.warn('no valid cancel url')
      }
    },
    goToNextStep() {
      document.getElementById(constants.buttonPaymentSubmitId).click()
    },
    onClickEdit() {
      this.editProfileEnabled = !this.editProfileEnabled
    },
    async onSubmitForm() {
      this.v$.$validate()

      if (this.v$.$invalid) {
        await this.$nextTick()
        scrollToValidation()
        return false
      }

      this.removeErrors()
      this.isLoading = true

      const postData = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        mobilePhone: this.mobilePhone,
        acceptTerms: this.terms,
        culture: document.documentElement.lang,
        userSessionId: this.store.bookingProgress.usersessionId,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointSubmitData,
        successCallback: this.handleFinalSubmitSuccess,
        errorCallback: this.handleFinalSubmitError,
        captchaAction: this.configuration.captchaAction,
        refreshTokenHandler: this.configuration.refresh,
      })

      this.isLoading = false
    },
    handleFinalSubmitSuccess(response) {
      this.isLoading = false
      this.removeErrors()

      const { redirectUrl } = response.data

      if (redirectUrl) {
        window.location.assign(redirectUrl)
      }
    },
    async handleFinalSubmitError(messages) {
      this.isLoading = false

      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage
      )

      await this.$nextTick()
      scrollToError()
    },
    removeErrors() {
      this.errorMsgs = []
    },
  },
}
</script>
